import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECT_ITEMS = [
  "A place at the table — not a marketing list",
  "Occasional updates when it truly matters",
  "Your details stay in confidence, never sold",
  "Unsubscribe any time, no questions asked",
];

const HOW_ITEMS = [
  {
    icon: "/icons/player-play.svg",
    title: "01 Episode notes",
    text: "A short note when a new conversation drops — Testimony Tuesday or the Faith & Politics track.",
  },
  {
    icon: "/icons/headphones.svg",
    title: "02 Community moments",
    text: "The occasional invite, story, or update when there is something worth pausing for.",
  },
  {
    icon: "/icons/mic-muted.svg",
    title: "03 No newsletter, no noise",
    text: "This is a community, not a mailing list. Your details are kept in confidence and never sold.",
  },
];

const Community = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(`${API}/community`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setStatus("success");
      setMessage(data.message);
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Something went wrong — please try again.");
    }
  };

  return (
    <main className="bg-white">
      {/* Hero + form */}
      <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 lg:px-[60px]">
            <div className="flex flex-col gap-5">
              <Words text="Join the Community" mode="load" delay={0.1} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]" />
              <h1 className="font-display text-[38px] leading-[1.08] text-black sm:text-[52px] lg:text-[62px] lg:leading-[68px]" aria-label="Come Sit With Us">
                <span className="block">
                  <Words text="Come Sit" mode="load" delay={0.2} stagger={0.08} />
                </span>
                <span className="block">
                  <Words text="With Us" mode="load" delay={0.38} stagger={0.08} />
                </span>
              </h1>
              <FadeUp mode="load" delay={0.55}>
                <p className="max-w-[480px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">
                  Share your name and email to be part of Stephanie’s community. We’ll hold onto your details for occasional updates that matter — this isn’t a
                  newsletter, and there’s nothing to buy.
                </p>
              </FadeUp>
              <FadeUp mode="load" delay={0.65} className="group relative aspect-[572/334] w-full max-w-[520px] overflow-hidden rounded-3xl">
                <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.05]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                  <img alt="Friends gathered around a table in warm light" className="h-full w-full object-cover" src="/figma/community-photo.jpg" />
                </div>
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linen opacity-0 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-15" />
              </FadeUp>
            </div>

            <FadeUp mode="load" y={30} delay={0.3}>
              <div data-testid="community-form-card" className="w-full rounded-3xl border border-hairline bg-white-smoke p-8 sm:p-10 lg:p-12">
                {status === "success" ? (
                  <div data-testid="community-success-message" className="flex flex-col items-start gap-4 py-6">
                    <span className="grid h-12 w-12 place-items-center rounded-full bg-linen font-display text-[20px] text-ink">✓</span>
                    <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[32px]">You’re in.</h2>
                    <p className="text-[15px] leading-[26px] text-charcoal">{message}</p>
                    <div className="mt-2 flex flex-wrap gap-4">
                      <PillButton href="/episodes" dataTestId="community-success-listen-button">
                        Start Listening
                      </PillButton>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="flex flex-col gap-5" data-testid="community-form">
                    <div className="flex flex-col gap-2">
                      <span className="eyebrow text-[12px] leading-[20px] text-charcoal-mid">Reserve your seat</span>
                      <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">Two fields. Ninety seconds. You’re in.</h2>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="community-name" className="text-[13px] font-medium text-charcoal-dark">
                        Full name
                      </label>
                      <input
                        id="community-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        data-testid="community-name-input"
                        className="h-12 rounded-xl border border-hairline bg-white px-4 text-[15px] text-ink outline-none transition-colors placeholder:text-charcoal-mid focus:border-silver"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="community-email" className="text-[13px] font-medium text-charcoal-dark">
                        Email address
                      </label>
                      <input
                        id="community-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="community-email-input"
                        className="h-12 rounded-xl border border-hairline bg-white px-4 text-[15px] text-ink outline-none transition-colors placeholder:text-charcoal-mid focus:border-silver"
                      />
                    </div>
                    {status === "error" && (
                      <p data-testid="community-error-message" className="text-[13.5px] leading-[22px] text-red-700">
                        {message}
                      </p>
                    )}
                    <button
                      type="submit"
                      data-testid="community-submit-button"
                      disabled={status === "loading"}
                      className="group relative inline-flex h-12 w-full items-center justify-center overflow-hidden rounded-full bg-ink text-[15px] uppercase tracking-[0.08em] text-linen transition-colors duration-500 hover:text-ink disabled:cursor-wait font-display"
                    >
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-linen [clip-path:circle(0%_at_50%_50%)] group-hover:[clip-path:circle(75%_at_50%_50%)] transition-[clip-path] duration-[650ms]"
                        style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
                      />
                      <span className="relative z-10">{status === "loading" ? "Saving your seat…" : "Join the Community"}</span>
                    </button>
                    <p className="text-[12.5px] leading-[21px] text-charcoal-mid">
                      By joining you agree we may hold your details for future community updates.
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12.9px] leading-[22.4px] text-charcoal">
                      {["No spam", "Occasional updates", "Unsubscribe any time"].map((t) => (
                        <span key={t} className="flex items-center gap-1">
                          <span className="text-[14px] font-semibold text-charcoal-dark">✓</span> {t}
                        </span>
                      ))}
                    </div>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* What you can expect */}
      <section className="w-full bg-white py-16 sm:py-20 lg:py-[88px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 lg:px-[60px]">
            <div className="flex flex-col gap-5">
              <FadeUp y={14}>
                <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">What you can expect</span>
              </FadeUp>
              <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[34px] lg:text-[40px]">
                <Words text="A small, quiet cadence." />
              </h2>
              <FadeUp y={16}>
                <p className="max-w-[460px] text-[15px] leading-[26px] text-charcoal">
                  Enough to stay connected, never enough to feel crowded. Your seat here is protected — this list stays with Stephanie and never leaves the
                  table.
                </p>
              </FadeUp>
              <div className="mt-2 flex flex-col gap-3">
                {EXPECT_ITEMS.map((t, i) => (
                  <FadeUp key={t} delay={0.05 * i} y={12}>
                    <div className="flex items-center gap-3 rounded-xl border border-hairline bg-white px-5 py-4">
                      <span className="font-display text-[15px] font-semibold text-charcoal-dark">✓</span>
                      <span className="text-[14.4px] leading-[24px] text-charcoal-dark">{t}</span>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-8">
              <FadeUp y={14}>
                <span className="eyebrow block text-[12px] leading-[20px] text-charcoal-mid">What to Expect</span>
              </FadeUp>
              <h2 className="font-display text-[28px] leading-[1.15] text-black sm:text-[34px] lg:text-[40px]">
                <Words text="How the community actually works" />
              </h2>
              <FadeUp y={16}>
                <p className="max-w-[460px] text-[15px] leading-[26px] text-charcoal">
                  Three simple promises. Nothing hidden, nothing to unwind later.
                </p>
              </FadeUp>
              <div className="flex flex-col divide-y divide-silver-line">
                {HOW_ITEMS.map((h, i) => (
                  <FadeUp key={h.title} delay={0.06 * i} y={14}>
                    <div className="flex items-start gap-5 py-6" data-testid={`community-expect-${i + 1}`}>
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-linen">
                        <img alt="" aria-hidden="true" src={h.icon} className="h-5 w-5" />
                      </span>
                      <div className="flex flex-col gap-1">
                        <p className="font-display text-[18px] leading-[26px] text-black">{h.title}</p>
                        <p className="text-[14px] leading-[24px] text-charcoal">{h.text}</p>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Not ready yet */}
      <section className="w-full bg-white-smoke py-16 sm:py-20">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col items-start gap-8 rounded-3xl border border-hairline bg-white p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:px-[60px]">
            <div className="flex max-w-[560px] flex-col gap-3">
              <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[32px]">
                <Words text="Not ready yet? Start with a listen." />
              </h2>
              <FadeUp y={14}>
                <p className="text-[14.6px] leading-[26px] text-charcoal sm:text-[15.2px]">
                  The best way to know the community is to hear the conversations that shape it.
                </p>
              </FadeUp>
            </div>
            <FadeUp y={12} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <PillButton href="/episodes" dataTestId="community-browse-episodes-button">
                Browse Episodes
              </PillButton>
              <Link to="/causes" data-testid="community-causes-link" className="group inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                <span className="transition-transform duration-500 ease-out group-hover:-translate-x-1">See the causes she stands with</span>
                <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">→</span>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Community;
